import { inject, Injectable, signal } from '@angular/core';
import { PointTechModePayload, RegisterUserFormTechModePayload, RegisterUserFormTechModeResponse, RegisterUserFormValues } from '../interfaces';
import { catchError, finalize, map, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { RestService } from 'src/app/services';
import { Router } from '@angular/router';
import { customRouteAdapter } from '../../custom-route/adapters/custom-route.adapter';
import { CustomRouteService } from '../../custom-route/services';
import { DailyRouteResponse } from '../../custom-route/interfaces/custom-route.response';

@Injectable({
  providedIn: 'root',
})
export class RegisterUserFormTechMode {
  // Service injections
  private _http = inject(HttpClient);
  private _router = inject(Router);
  private _customRouteService = inject(CustomRouteService);
  private _restService = inject(RestService);

  // State signals
  private _isLoadingRegisterUserTechModeForRecommendedRoute = signal<boolean>(false);
  public isLoadingRegisterUserTechModeForRecommendedRoute = this._isLoadingRegisterUserTechModeForRecommendedRoute.asReadonly();
  
  private _isErrorRegisterUserTechModeForRecommendedRoute = signal<boolean>(false);
  public isErrorRegisterUserTechModeForRecommendedRoute = this._isErrorRegisterUserTechModeForRecommendedRoute.asReadonly();
  
  // Service state signals
  private _device = this._restService.device;
  
  // Http requests
  public sendRegisterUserTechModeForRecommendedRouteServiceSignal(body: RegisterUserFormTechModePayload): void {
    this._sendRegisterUserTechModeForRecommendedRoute(body).subscribe({
      next: (response: RegisterUserFormTechModeResponse) => {
        
        const adaptedRouteData = customRouteAdapter(response.id, response.dailyRoutes as DailyRouteResponse[]);
        this._customRouteService.setOriginalCustomRouteData(adaptedRouteData);
        
        this._navigateToCustomRoute(response.token);
      },
      error: (_error: HttpErrorResponse) => {
        console.error('Error sending register user tech mode for recommended route:', _error);
        this._isErrorRegisterUserTechModeForRecommendedRoute.set(true);
      }
    });
  }

  private _sendRegisterUserTechModeForRecommendedRoute(body: RegisterUserFormTechModePayload): Observable<RegisterUserFormTechModeResponse> {
    this._isLoadingRegisterUserTechModeForRecommendedRoute.set(true);
    
    const url = `${environment.apiAssistantUrlBase}/api/recommended-route`;
    return this._http.post<RegisterUserFormTechModeResponse>(url, body)
      .pipe(
        map((response: RegisterUserFormTechModeResponse) => {
          this._isLoadingRegisterUserTechModeForRecommendedRoute.set(false);
          return response;
        }),
        catchError(error => throwError(() => new Error('HTTP request failed', { cause: error }))),
        finalize(() => this._isLoadingRegisterUserTechModeForRecommendedRoute.set(false))
    );
  }
  
  // Methods
  public getRegisterUserTechModeForRecommendedRoutePayload(formValues: RegisterUserFormValues, pointList: PointTechModePayload[]): RegisterUserFormTechModePayload {
    if (!this._device()) return;
    return {
      subdomain: this._restService.subDomain,
      client_id: this._device().client || this._device().distributorID,
      client_type: this._device().type,
      app_id: this._device().appID,
      start_date: formValues.startDate,
      end_date: formValues.endDate,
      start_time: formValues.startTime || undefined,
      start_location: {
        address: formValues.startLocationAddress,
        lat: formValues.startLocationLat,
        long: formValues.startLocationLng,
      },
      point_list: pointList,
      private: formValues.privacy === true,
    };
  }
  
  private _navigateToCustomRoute(token: string): void {
    const targetUrl = `/custom-route/v3/${token}`;
    
    this._router.navigateByUrl(targetUrl);
  }
}
  